import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MessageCircle, PawPrint } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeaturedMemories } from '@/hooks/usePosts';
import PostCard from './PostCard';

/**
 * Autoplays a muted, inline video when it enters the viewport (Instagram-style),
 * mirroring the feed's MediaCarousel behavior. Pauses when scrolled away.
 */
const AutoPlayVideo = ({ src }: { src: string }) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
          } else {
            el.pause();
            setPlaying(false);
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <video
        ref={ref}
        src={`${src}#t=0.1`}
        className="w-full h-full object-cover bg-black"
        muted
        loop
        playsInline
        // @ts-ignore
        webkit-playsinline="true"
        preload="metadata"
      />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-12 w-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-white fill-current ml-0.5">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
    </>
  );
};


interface FeaturedMemoriesGridProps {
  limit?: number;
  linkToPost?: boolean;
  className?: string;
  showLostAlerts?: boolean;
}

const FeaturedMemoriesGrid = ({ limit = 9, linkToPost = true, className, showLostAlerts = false }: FeaturedMemoriesGridProps) => {
  const { t } = useTranslation();
  // When the grid is not set up to render alerts, don't fetch them at all.
  const { data, isLoading } = useFeaturedMemories(limit, !showLostAlerts);

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className ?? ''}`}>
        {Array.from({ length: limit }).map((_, i) => (
          <div key={i} className="bg-card rounded-2xl overflow-hidden shadow-sm">
            <Skeleton className="w-full aspect-square" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-10">
        <PawPrint className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">{t('landing.noMemoriesYet', 'No memories yet')}</p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className ?? ''}`}>
      {data.map((post: any) => {
        if (showLostAlerts && post.post_type === 'lost_pet') {
          return <div key={post.id} className="col-span-full"><PostCard post={post} /></div>;
        }
        const media = post.media?.[0];
        const petName = post.pet?.name || 'Pet';
        const isVideo = media?.media_type?.startsWith('video');
        const card = (
          <div className="bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-border/50">
            <div className="relative w-full aspect-square bg-muted">
              {media?.url ? (
                isVideo ? (
                  <AutoPlayVideo src={media.url} />

                ) : (
                  <img
                    src={media.url}
                    alt={petName}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PawPrint className="h-10 w-10 text-muted-foreground/40" />
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-sm truncate">{petName}</h3>
                <span className="text-xs bg-pet-purple/10 text-pet-purple px-2 py-0.5 rounded-full font-medium">
                  {t('landing.featured', 'Featured')}
                </span>
              </div>
              {post.caption && (
                <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{post.caption}</p>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={post.pet?.photo_url} />
                    <AvatarFallback>{petName.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-muted-foreground truncate">
                    {post.pet?.species || 'Pet'}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <span className="flex items-center gap-1 text-xs">
                    <Heart className="h-3.5 w-3.5" />
                    {post.likes_count || 0}
                  </span>
                  <span className="flex items-center gap-1 text-xs">
                    <MessageCircle className="h-3.5 w-3.5" />
                    {post.comments_count || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
        return linkToPost ? (
          <Link key={post.id} to={`/post/${post.id}`} className="block">
            {card}
          </Link>
        ) : (
          <div key={post.id}>{card}</div>
        );
      })}
    </div>
  );
};

export default FeaturedMemoriesGrid;
